import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { FilmstripTabs } from '../components/UI'
import { useNavigate } from 'react-router-dom'
import { Search as SearchIcon } from 'lucide-react'
import { tmdbGet } from '../lib/tmdb'
import { preferEnglish } from '../lib/preferEnglish'
import { clickable } from '../lib/a11y'
import { safeStorage } from '../lib/safeStorage'
import { logEvent } from '../lib/analytics'
import { ShowCard } from '../components/ShowCard'
import { PosterSkeleton } from '../components/Skeleton'

// ── Discover 17 ───────────────────────────────────────────────────────────────
// Breathing-room rebuild of the 16.x Discover (three-row sticky header, mood
// chips, service/genre/decade filters, nine curated shelves, social hero cards).
// This page has one job: browse. A masthead, one tappable search field that
// opens the real global search, one row of moods, one "on my services" switch,
// and a single grid that answers. Curation lives in the moods; nothing stacks.

const MOODS = [
  { id: 'funny',      emoji: '😂', label: 'Funny',      genres: [35] },
  { id: 'intense',    emoji: '🔥', label: 'Intense',    genres: [80, 18] },
  { id: 'cozy',       emoji: '🛋️', label: 'Cozy',       genres: [10751, 18] },
  { id: 'epic',       emoji: '🚀', label: 'Epic',       genres: [10765, 10759] },
  { id: 'mysterious', emoji: '🔍', label: 'Mysterious', genres: [9648, 80] },
  { id: 'moving',     emoji: '😢', label: 'Moving',     genres: [18] },
  { id: 'real',       emoji: '🎥', label: 'Real',       genres: [99, 10764] },
  { id: 'animated',   emoji: '✏️', label: 'Animated',   genres: [16] },
]
const MOOD_KEY = 'nextep_discover17_mood'
const MINE_KEY = 'nextep_discover17_mine'

export default function Discover17({ wl, myServices, user }) {
  const navigate = useNavigate()
  const [searchDraft, setSearchDraft] = useState('')
  const [mood, setMood] = useState(() => { try { return safeStorage.get(MOOD_KEY) || null } catch { return null } })
  const [mine, setMine] = useState(() => { try { return safeStorage.get(MINE_KEY) === '1' } catch { return false } })
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const reqSeq = useRef(0)
  const serviceIds = useMemo(() => (myServices?.list || []).map(s => s.id), [myServices?.list])
  const hasServices = serviceIds.length > 0
  const pickMood = (id) => { const next = mood === id ? null : id; setMood(next); try { next ? safeStorage.set(MOOD_KEY, next) : safeStorage.set(MOOD_KEY, '') } catch {} }
  const toggleMine = () => { const n = !mine; setMine(n); try { safeStorage.set(MINE_KEY, n ? '1' : '0') } catch {} }

  const load = useCallback(async (pg = 1, append = false) => {
    const seq = ++reqSeq.current
    if (pg === 1) { setLoading(true); setError(false); setItems([]) } else setLoadingMore(true)
    try {
      const m = MOODS.find(x => x.id === mood)
      let data
      if (!m && !(mine && hasServices)) {
        data = await tmdbGet('/trending/tv/week', { page: pg })
        data = { ...data, results: preferEnglish(data?.results) }
      } else {
        const params = { sort_by: 'popularity.desc', page: pg, include_adult: false, 'vote_count.gte': 50 }
        if (m) params.with_genres = m.genres.join('|')
        if (mine && hasServices) { params.with_watch_providers = serviceIds.join('|'); params.watch_region = 'US'; params.with_watch_monetization_types = 'flatrate' }
        data = await tmdbGet('/discover/tv', params)
      }
      if (seq !== reqSeq.current) return
      const res = (data.results || []).filter(s => s.poster_path)
      setItems(prev => {
        if (!append) return res
        const seen = new Set(prev.map(s => s.id))
        return [...prev, ...res.filter(s => !seen.has(s.id))]
      })
      setHasMore(pg < Math.min(5, data.total_pages || 1))
      setPage(pg)
      if (pg === 1) logEvent('discover_browse', { mood: mood || 'trending', mine: mine && hasServices })
    } catch { if (pg === 1 && seq === reqSeq.current) setError(true) }
    finally { if (seq === reqSeq.current) { setLoading(false); setLoadingMore(false) } }
  }, [mood, mine, hasServices, serviceIds])

  useEffect(() => { load(1, false) }, [load])

  const title = mood ? MOODS.find(x => x.id === mood)?.label : (mine && hasServices ? 'Popular on your services' : 'Trending this week')

  return (
    <div className="surface-wide p17" style={{ minHeight: '100dvh', background: 'var(--bg)', fontFamily: 'var(--font)', paddingBottom: 'calc(var(--nav-h) + 24px)' }}>
      <div style={{ padding: 'max(24px, calc(env(safe-area-inset-top) + 8px)) 20px 0' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 28, lineHeight: 1.1, color: 'var(--text-strong)', letterSpacing: '-0.01em' }}>Discover</h1>
        {/* A REAL input, not a button styled like one (Jason 2026-09-17: "takes you
            to a second text input" — the old <button> looked exactly like a text
            field, so a tap read as "type here," but nothing could actually be
            typed until landing on /search and its OWN input picked up focus. On
            mobile that second focus is a plain JS .focus() inside a setTimeout,
            not inside the tap's own event handler, and browsers only pop the
            keyboard for a focus that's synchronous with the gesture — so the
            keyboard silently never opened and it took a second real tap to type
            anything. A real input here gets the keyboard on the very first tap,
            no timing race possible. Submitting (Enter, or the icon) hands off to
            the real global search page with the query already filled in — still
            ONE search engine, just reached through a field that works instead of
            a fake one that doesn't. */}
        <form onSubmit={(e) => { e.preventDefault(); navigate(`/search${searchDraft.trim() ? `?q=${encodeURIComponent(searchDraft.trim())}` : ''}`) }}
          style={{ marginTop: 16, width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>
          <button type="submit" aria-label="Search" className="ui-hit" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-dim)', display: 'flex', flexShrink: 0, cursor: 'pointer' }}><SearchIcon size={15} /></button>
          <input value={searchDraft} onChange={e => setSearchDraft(e.target.value)} placeholder="Search any show"
            aria-label="Search shows, people and pages"
            style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 15, fontFamily: 'var(--font)' }} />
        </form>
        <FilmstripTabs value={mood} onChange={pickMood} style={{ marginTop: 16 }} tabs={MOODS.map(m => ({ value: m.id, label: <><span aria-hidden="true">{m.emoji}</span>{m.label}</> }))} />
      </div>

      <div style={{ margin: '24px 20px 0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }}>{title}</div>
        {hasServices && (
          <button onClick={toggleMine} aria-pressed={mine} className="ui-hit" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 12.5, color: mine ? 'var(--amber)' : 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: mine ? 'var(--amber)' : 'transparent', border: '1px solid currentColor', display: 'inline-block' }} />On my services
          </button>
        )}
        {hasServices && (
          <span {...clickable(() => navigate('/settings/streaming'), { label: 'Edit your streaming services' })} data-tip="Edit services" className="ui-hit" style={{ fontSize: 12.5, color: 'var(--text-dim)', cursor: 'pointer', flexShrink: 0 }}>Edit</span>
        )}
        {!hasServices && user && (
          <span {...clickable(() => navigate('/settings/streaming'), { label: 'Pick your streaming services' })} className="ui-hit" style={{ fontSize: 12.5, color: 'var(--text-dim)', cursor: 'pointer' }}>Pick your services →</span>
        )}
      </div>

      <div style={{ margin: '14px 12px 0' }}>
        {loading && items.length === 0 && <PosterSkeleton count={9} columns={3} style={{ padding: 0 }} />}
        {error && !loading && items.length === 0 && (
          <div style={{ padding: '40px 8px', color: 'var(--text-dim)', fontSize: 14 }}>Couldn’t reach the catalog. <span {...clickable(() => load(1, false), { label: 'Retry' })} style={{ color: 'var(--amber)', cursor: 'pointer' }}>Try again</span></div>
        )}
        {items.length > 0 && (
          <div className="cascade-in" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {items.map(show => <ShowCard key={show.id} show={show} wl={wl} compact />)}
          </div>
        )}
        {!loading && !error && items.length === 0 && <div style={{ padding: '40px 8px', color: 'var(--text-dim)', fontSize: 14 }}>Nothing here for that mix. Try another mood.</div>}
        {hasMore && items.length > 0 && (
          <div style={{ padding: '18px 0 4px', textAlign: 'center' }}>
            <button onClick={() => { if (!loadingMore) load(page + 1, true) }} disabled={loadingMore} aria-busy={loadingMore} style={{ minHeight: 44, opacity: loadingMore ? 0.6 : 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', padding: '10px 24px', color: 'var(--text-muted)', fontWeight: 500, fontSize: 13, cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'var(--font)' }}>{loadingMore ? 'Loading…' : 'More'}</button>
          </div>
        )}
      </div>
    </div>
  )
}
